import { useMemo, useRef, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import type { DateSelectArg, EventClickArg } from "@fullcalendar/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { CalendarPlus } from "lucide-react";
import {
  APPOINTMENT_STATUS_LABELS,
  APPOINTMENT_TYPE_LABELS,
  AppointmentStatusSchema,
  CreateAppointmentSchema,
  type Appointment,
  type AppointmentStatus,
  type CreateAppointmentInput,
} from "@clinic/types";
import { appointmentTypeColors } from "@clinic/ui";
import { api } from "@/lib/api";
import { useMe } from "@/lib/auth";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { FieldError, Input, Label, Select, Textarea } from "@/components/ui/input";

function minutesToTime(min: number): string {
  return `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;
}

export function AppointmentsPage() {
  const queryClient = useQueryClient();
  const me = useMe();
  const calendarRef = useRef<FullCalendar>(null);
  const [range, setRange] = useState<{ from: string; to: string } | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [selected, setSelected] = useState<Appointment | null>(null);

  const appointments = useQuery({
    queryKey: ["appointments", range],
    queryFn: () => api.appointments.list(range ?? undefined),
    enabled: range !== null,
  });
  const availability = useQuery({
    queryKey: ["availability"],
    queryFn: () => api.settings.getAvailability(),
  });
  const patients = useQuery({ queryKey: ["patients", "", false], queryFn: () => api.patients.list() });
  const doctors = useQuery({ queryKey: ["doctors"], queryFn: () => api.users.doctors() });

  const form = useForm<CreateAppointmentInput>({
    resolver: zodResolver(CreateAppointmentSchema),
    defaultValues: { type: "ROUTINE" },
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["appointments"] });

  const create = useMutation({
    mutationFn: (input: CreateAppointmentInput) => api.appointments.create(input),
    onSuccess: () => {
      invalidate();
      setCreateOpen(false);
      form.reset({ type: "ROUTINE" });
    },
  });
  const updateStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: AppointmentStatus }) =>
      api.appointments.update(id, { status }),
    onSuccess: (updated) => {
      invalidate();
      setSelected(updated);
    },
  });
  const cancel = useMutation({
    mutationFn: (id: string) => api.appointments.cancel(id),
    onSuccess: () => {
      invalidate();
      setSelected(null);
    },
  });

  const events = useMemo(
    () =>
      (appointments.data ?? []).map((a) => ({
        id: a.id,
        title: `${a.patient?.fullName ?? "Patient"} · ${APPOINTMENT_TYPE_LABELS[a.type]}`,
        start: a.startTime,
        end: a.endTime,
        backgroundColor: appointmentTypeColors[a.type] ?? "#1D9E75",
        textColor: "#0a1410",
        classNames: a.status === "CANCELLED" ? ["opacity-40", "line-through"] : [],
      })),
    [appointments.data],
  );

  const businessHours = useMemo(
    () =>
      (availability.data ?? []).map((b) => ({
        daysOfWeek: [b.dayOfWeek],
        startTime: minutesToTime(b.startMinute),
        endTime: minutesToTime(b.endMinute),
      })),
    [availability.data],
  );

  const defaultDoctorId =
    me.data?.role === "DOCTOR" ? me.data.id : doctors.data?.[0]?.id ?? "";

  const openCreate = (start?: Date, end?: Date) => {
    form.reset({
      type: "ROUTINE",
      doctorId: defaultDoctorId,
      startTime: start ? format(start, "yyyy-MM-dd'T'HH:mm") : "",
      endTime: end ? format(end, "yyyy-MM-dd'T'HH:mm") : "",
      patientId: "",
      notes: "",
    });
    create.reset();
    setCreateOpen(true);
  };

  return (
    <div>
      <PageHeader
        title="Appointments"
        subtitle="Select an empty slot to book, click an appointment to manage it"
        actions={
          <Button onClick={() => openCreate()}>
            <CalendarPlus size={15} /> New appointment
          </Button>
        }
      />

      <div
        className="rounded-card border bg-bg-secondary p-4"
        style={{ borderColor: "var(--accent-subtle)" }}
      >
        <FullCalendar
          ref={calendarRef}
          plugins={[timeGridPlugin, dayGridPlugin, interactionPlugin]}
          initialView="timeGridWeek"
          headerToolbar={{
            left: "prev,next today",
            center: "title",
            right: "timeGridDay,timeGridWeek,dayGridMonth",
          }}
          height="auto"
          firstDay={0}
          slotMinTime="07:00:00"
          slotMaxTime="21:00:00"
          allDaySlot={false}
          nowIndicator
          selectable
          selectMirror
          businessHours={businessHours}
          events={events}
          datesSet={(arg) =>
            setRange({ from: arg.start.toISOString(), to: arg.end.toISOString() })
          }
          select={(arg: DateSelectArg) => openCreate(arg.start, arg.end)}
          eventClick={(arg: EventClickArg) => {
            const appointment = appointments.data?.find((a) => a.id === arg.event.id);
            if (appointment) setSelected(appointment);
          }}
        />
      </div>

      {/* legend */}
      <div className="mt-3 flex flex-wrap gap-4 text-xs text-text-secondary">
        {Object.entries(APPOINTMENT_TYPE_LABELS).map(([type, label]) => (
          <span key={type} className="flex items-center gap-1.5">
            <span
              className="h-2.5 w-2.5 rounded-sm"
              style={{ background: appointmentTypeColors[type] }}
            />
            {label}
          </span>
        ))}
      </div>

      {/* create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent title="New appointment">
          <form onSubmit={form.handleSubmit((v) => create.mutate(v))} className="space-y-4">
            <div>
              <Label>Patient</Label>
              <Select {...form.register("patientId")}>
                <option value="">Select patient…</option>
                {(patients.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.fullName} ({p.idNumber})
                  </option>
                ))}
              </Select>
              <FieldError message={form.formState.errors.patientId?.message} />
            </div>
            {(doctors.data ?? []).length > 1 ? (
              <div>
                <Label>Doctor</Label>
                <Select {...form.register("doctorId")}>
                  {(doctors.data ?? []).map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </Select>
              </div>
            ) : (
              <input type="hidden" {...form.register("doctorId")} />
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start</Label>
                <Input type="datetime-local" {...form.register("startTime")} />
                <FieldError message={form.formState.errors.startTime?.message} />
              </div>
              <div>
                <Label>End</Label>
                <Input type="datetime-local" {...form.register("endTime")} />
                <FieldError message={form.formState.errors.endTime?.message} />
              </div>
            </div>
            <div>
              <Label>Type</Label>
              <Select {...form.register("type")}>
                {Object.entries(APPOINTMENT_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea {...form.register("notes")} rows={2} />
            </div>
            {create.isError && (
              <p className="rounded-input border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
                {create.error instanceof Error ? create.error.message : "Booking failed"}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setCreateOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" loading={create.isPending}>
                Book appointment
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* detail dialog */}
      <Dialog open={selected !== null} onOpenChange={(open) => !open && setSelected(null)}>
        {selected && (
          <DialogContent title={selected.patient?.fullName ?? "Appointment"}>
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-text-secondary">When</span>
                <span>
                  {format(new Date(selected.startTime), "EEE, d MMM yyyy")}{" "}
                  <span className="font-mono">
                    {format(new Date(selected.startTime), "HH:mm")}–
                    {format(new Date(selected.endTime), "HH:mm")}
                  </span>
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-text-secondary">Type</span>
                <span>{APPOINTMENT_TYPE_LABELS[selected.type]}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-text-secondary">Doctor</span>
                <span>{selected.doctor?.name}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-text-secondary">Phone</span>
                <span>{selected.patient?.phone}</span>
              </div>
              {selected.notes && (
                <p className="rounded-lg bg-bg px-3 py-2 text-text-secondary">{selected.notes}</p>
              )}
              <div className="flex items-center justify-between">
                <span className="text-text-secondary">Status</span>
                <Select
                  className="w-44"
                  value={selected.status}
                  onChange={(e) =>
                    updateStatus.mutate({
                      id: selected.id,
                      status: AppointmentStatusSchema.parse(e.target.value),
                    })
                  }
                >
                  {Object.entries(APPOINTMENT_STATUS_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </div>
              {selected.reminderSent24h && <Badge tone="neutral">24h reminder sent</Badge>}
            </div>
            <div className="mt-5 flex justify-between">
              <Button
                variant="danger"
                size="sm"
                loading={cancel.isPending}
                disabled={selected.status === "CANCELLED"}
                onClick={() => cancel.mutate(selected.id)}
              >
                Cancel appointment
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setSelected(null)}>
                Close
              </Button>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
